/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls including login,
 * logout, token refresh, and user profile operations
 */

import { api, handleApiError } from './api';
import { getUserFriendlyAuthError } from '@/utils/authErrors';
import type { LoginCredentials, AuthResponse, User } from '@/types';

/**
 * Authentication Service Object
 * Contains all auth-related API operations
 */
export const authService = {
  /**
   * User Login
   * Authenticates user with username/password credentials
   * 
   * @param credentials - User login credentials
   * @returns Promise with authentication response
   */
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const response = await api.post<AuthResponse>(
        '/auth/login', 
        {
          username: credentials.email,
          password: credentials.password,
          rememberMe: credentials.rememberMe || false,
        },
        {
          // Skip token refresh for login requests - 401 means invalid credentials
          skipTokenRefresh: true,
        }
      );

      return response.data;
    } catch (error) {
      // Use user-friendly error message instead of technical details
      const userFriendlyMessage = getUserFriendlyAuthError(error);
      throw new Error(userFriendlyMessage);
    }
  },

  /**
   * User Logout
   * Invalidates current session and tokens on server
   * 
   * @returns Promise that resolves when logout is complete
   */
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Log error but don't throw - logout should always succeed locally
      console.warn('Logout API call failed:', error);
    }
  },

  /**
   * Token Refresh
   * Refreshes access token using httpOnly refresh cookie
   * No parameters needed - refresh token is sent automatically via cookie
   * 
   * @returns Promise that resolves when token is refreshed
   */
  refreshToken: async (): Promise<void> => {
    try {
      await api.post(
        '/auth/refresh',
        {}, // No body needed - refresh token is in httpOnly cookie
        {
          // Skip token refresh for refresh requests - prevent infinite loops
          skipTokenRefresh: true,
        }
      );
      // New access token cookie is set automatically by the server
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Current User Profile
   * Fetches current authenticated user's profile data
   * 
   * @returns Promise with user profile data
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await api.get<{ user: User }>('/auth/me');
      return response.data.user;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Update User Profile
   * Updates current user's profile information
   * 
   * @param userData - Partial user data to update
   * @returns Promise with updated user data
   */
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    try {
      const response = await api.put<{ user: User }>('/auth/profile', userData);
      return response.data.user;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Change Password
   * Updates user's password
   * 
   * @param passwords - Current and new password
   * @returns Promise that resolves when password is changed
   */
  changePassword: async (passwords: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> => {
    try {
      await api.put('/auth/password', passwords);
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Request Password Reset
   * Sends password reset email to user
   * 
   * @param email - User's email address
   * @returns Promise that resolves when reset email is sent
   */
  requestPasswordReset: async (email: string): Promise<void> => {
    try {
      await api.post(
        '/auth/forgot-password',
        { email },
        {
          // Skip token refresh - user might not be authenticated
          skipTokenRefresh: true,
        }
      );
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Reset Password
   * Resets password using reset token
   * 
   * @param resetData - Reset token and new password
   * @returns Promise that resolves when password is reset
   */
  resetPassword: async (resetData: {
    token: string;
    newPassword: string;
  }): Promise<void> => {
    try {
      await api.post(
        '/auth/reset-password',
        resetData,
        {
          // Skip token refresh - user is resetting password, might not have valid token
          skipTokenRefresh: true,
        }
      );
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Verify Email
   * Verifies user's email address using verification token
   * 
   * @param token - Email verification token
   * @returns Promise that resolves when email is verified
   */
  verifyEmail: async (token: string): Promise<void> => {
    try {
      await api.post('/auth/verify-email', { token });
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Resend Email Verification
   * Sends a new email verification link
   * 
   * @returns Promise that resolves when verification email is sent
   */
  resendEmailVerification: async (): Promise<void> => {
    try {
      await api.post('/auth/resend-verification');
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },
};

export default authService;